import {
  completeSetupRequestSchema,
  moduleCompleteSetupResultSchema,
  moduleInstallResultSchema,
  moduleRemoveResultSchema,
  moduleStoreResponseSchema,
  moduleUpgradeResultSchema,
  type CompleteSetupRequest,
  type ProductCode,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { meQueryKey } from '@/platform/auth/use-me';
import { setupStatusQueryKey } from '@/platform/brand/use-setup';

export const moduleStoreQueryKey = ['modules'] as const;

export function useModuleStore(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: moduleStoreQueryKey,
    queryFn: () => apiFetch('/modules', moduleStoreResponseSchema),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: true,
  });
}

function invalidateModuleQueries(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: moduleStoreQueryKey }),
    queryClient.invalidateQueries({ queryKey: meQueryKey }),
    queryClient.invalidateQueries({ queryKey: setupStatusQueryKey }),
  ]);
}

export function useInstallModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: ProductCode) =>
      apiFetch(`/modules/${code}/install`, moduleInstallResultSchema, { method: 'POST' }),
    onSuccess: async (data) => {
      queryClient.setQueryData(moduleStoreQueryKey, { items: data.items });
      await invalidateModuleQueries(queryClient);
    },
  });
}

export function useRemoveModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: ProductCode) =>
      apiFetch(`/modules/${code}/remove`, moduleRemoveResultSchema, { method: 'POST' }),
    onSuccess: async (data) => {
      queryClient.setQueryData(moduleStoreQueryKey, { items: data.items });
      await invalidateModuleQueries(queryClient);
    },
  });
}

export function useUpgradeModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: ProductCode) =>
      apiFetch(`/modules/${code}/upgrade`, moduleUpgradeResultSchema, { method: 'POST' }),
    onSuccess: async (data) => {
      queryClient.setQueryData(moduleStoreQueryKey, { items: data.items });
      await invalidateModuleQueries(queryClient);
    },
  });
}

export function useCompleteSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CompleteSetupRequest) =>
      apiFetch('/setup', moduleCompleteSetupResultSchema, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(completeSetupRequestSchema.parse(body)),
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(moduleStoreQueryKey, { items: data.items });
      await invalidateModuleQueries(queryClient);
    },
  });
}
